import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import type { SummedValue } from './diversificationsApi';

/**
 * Advisor-only aggregated holdings roll-up.
 *
 * Backed by `GET /api/Advisor/AggregatedHoldings`
 * (`Areas/Advisor/Controllers/AggregatedHoldingsController.cs`). The processor
 * groups every holding across the advisor's book by CUSIP (falling back to
 * Symbol when no CUSIP exists), so one record can be held by many investors —
 * see `HeldBy`.
 *
 * There is no investor/administrator equivalent of this route, so the path is
 * hard-coded rather than role-aware.
 */

/** One investor holding a slice of an aggregated position. */
export interface AdvisorAggregatedHoldingHeldBy {
  InvestorId: number;
  InvestorName: string;
  /** `WealthHoldingId`s this investor holds for the aggregated key. */
  HoldingIds: number[];
}

export interface AdvisorAggregatedHolding {
  /** CUSIP when present, otherwise Symbol. Grouping key for the roll-up. */
  Key: string;
  Name: string;
  Balance: number;
  HeldBy: AdvisorAggregatedHoldingHeldBy[];
  /** Share of `Balance` held in home (advisor-managed) accounts, 0-100. */
  PercentHome: number;
  Symbol?: string;
  CUSIP?: string;
  /** Populated only when the CUSIP is a real CUSIP, not a synthetic id. */
  CUSIPDisplay?: string;
  /** Only set when the firm enables the `ShowPrimaryAdvisor` setting. */
  PrimaryAdvisorId?: number | null;
  /** `'Not Set'` when `ShowPrimaryAdvisor` is off or no primary is assigned. */
  PrimaryAdvisorName: string;
}

/**
 * Note: this endpoint returns `SummedValues` (an array, always summing
 * `Balance`), not the single `SummedValue` of `RecordResponse<T>`.
 */
export interface AdvisorAggregatedHoldingsResponse {
  Records: AdvisorAggregatedHolding[];
  TotalLength: number;
  SummedValues: SummedValue[];
}

export interface AdvisorAggregatedHoldingsSort {
  property: string;
  direction: 'asc' | 'desc';
  order: number;
}

export interface ExcludedGroup {
  GroupId: string;
  ExcludedIds: string[];
}

/** Mirrors `AggregatedHoldingsRequest` : `AdvisorAccountFilters`. */
export interface GetAdvisorAggregatedHoldingsQueryArg {
  searchTerm?: string;
  /** Backend defaults to 500. */
  limit?: number;
  page?: number;
  sort?: AdvisorAggregatedHoldingsSort[];
  InvestorId?: number;
  GroupId?: number;
  ClassificationId?: number;
  InstitutionId?: number;
  WealthId?: number;
  ShowHome?: boolean;
  ShowAway?: boolean;
  ExcludedInstitutions?: number[];
  ExcludedCategories?: number[];
  ExcludedAccountTypes?: number[];
  ExcludedGroups?: ExcludedGroup[];
  OnlyPrimaryInvestors?: boolean;
  /** Set by report exports; makes the backend track the returned CUSIPs. */
  IsReport?: boolean;
}

function sortParamKey(
  index: number,
  field: 'Property' | 'Direction' | 'Order',
): string {
  return `sort[${String(index)}].${field}`;
}

function buildAdvisorAggregatedHoldingsParams(
  arg: GetAdvisorAggregatedHoldingsQueryArg,
): Record<string, string | number | boolean> {
  const {
    searchTerm,
    limit,
    page,
    sort,
    InvestorId,
    GroupId,
    ClassificationId,
    InstitutionId,
    WealthId,
    ShowHome,
    ShowAway,
    ExcludedInstitutions,
    ExcludedCategories,
    ExcludedAccountTypes,
    ExcludedGroups,
    OnlyPrimaryInvestors,
    IsReport,
  } = arg;

  const params: Record<string, string | number | boolean> = {
    ...(typeof searchTerm !== 'undefined' ? { searchTerm } : {}),
    ...(typeof limit !== 'undefined' ? { limit } : {}),
    ...(typeof page !== 'undefined' ? { page } : {}),
    ...(typeof InvestorId !== 'undefined' ? { InvestorId } : {}),
    ...(typeof GroupId !== 'undefined' ? { GroupId } : {}),
    ...(typeof ClassificationId !== 'undefined' ? { ClassificationId } : {}),
    ...(typeof InstitutionId !== 'undefined' ? { InstitutionId } : {}),
    ...(typeof WealthId !== 'undefined' ? { WealthId } : {}),
    ...(typeof ShowHome !== 'undefined' ? { ShowHome } : {}),
    ...(typeof ShowAway !== 'undefined' ? { ShowAway } : {}),
    ...(typeof OnlyPrimaryInvestors !== 'undefined'
      ? { OnlyPrimaryInvestors }
      : {}),
    ...(typeof IsReport !== 'undefined' ? { IsReport } : {}),
    ...(ExcludedInstitutions?.length
      ? { ExcludedInstitutions: ExcludedInstitutions.join(',') }
      : {}),
    ...(ExcludedCategories?.length
      ? { ExcludedCategories: ExcludedCategories.join(',') }
      : {}),
    ...(ExcludedAccountTypes?.length
      ? { ExcludedAccountTypes: ExcludedAccountTypes.join(',') }
      : {}),
    ...(ExcludedGroups?.length
      ? { ExcludedGroups: JSON.stringify(ExcludedGroups) }
      : {}),
  };

  if (sort?.length) {
    sort.forEach((s, i) => {
      params[sortParamKey(i, 'Property')] = s.property;
      params[sortParamKey(i, 'Direction')] = s.direction;
      params[sortParamKey(i, 'Order')] = s.order;
    });
  }

  return params;
}

export const advisorAggregatedHoldingsApi = createApi({
  reducerPath: 'advisorAggregatedHoldingsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AdvisorAggregatedHoldings'],
  endpoints: (builder) => ({
    getAdvisorAggregatedHoldings: builder.query<
      AdvisorAggregatedHoldingsResponse,
      GetAdvisorAggregatedHoldingsQueryArg
    >({
      query: (arg) => ({
        url: '/api/Advisor/AggregatedHoldings',
        method: 'GET',
        params: {
          ...buildAdvisorAggregatedHoldingsParams(arg),
          // Cache buster the legacy UI also sends; RTK's cache key comes from
          // `arg`, so this does not fragment the cache.
          _: Date.now(),
        },
      }),
      providesTags: [{ type: 'AdvisorAggregatedHoldings', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAdvisorAggregatedHoldingsQuery,
  useLazyGetAdvisorAggregatedHoldingsQuery,
} = advisorAggregatedHoldingsApi;
